import React, { useState, useEffect, useCallback, useContext } from "react";
import "../../assets/styles/arrows.css"; // import new arrow styles
import { getBaseStyleClasses } from '../../core/utils/baseStyleUtils';
import PreviewContext from "../../editor/context/PreviewContext";

// Helper: Retrieve image dimensions, falling back to natural dimensions.
const getDimensions = (imgObj) => {
  // If dimensions are provided in the object, use them.
  if (imgObj.width && imgObj.height)
    return { width: imgObj.width, height: imgObj.height };
  // Otherwise, use default natural dimensions.
  const tempImg = new Image();
  tempImg.src = imgObj.image;
  const naturalWidth = tempImg.naturalWidth || 1;
  const naturalHeight = tempImg.naturalHeight || 1;
  return { width: naturalWidth, height: naturalHeight };
};

// Helper: Retrieve transition styles based on the value of transition_style.
const getTransitionStyles = (style) => {
  switch (style) {
    case "fade":
      return { transition: "opacity 1s ease" };
    case "slide":
      return { transition: "transform 0.5s ease" };
    case "wipe":
      return { transition: "clip-path 0.5s ease" };
    case "zoom":
      return { transition: "transform 0.5s ease" };
    case "flip":
      return { transition: "transform 0.5s ease" };
    case "dissolve":
      return { transition: "opacity 0.8s ease" };
    case "none":
      return { transition: "none" };
    default:
      return { transition: "opacity 1s ease" };
  }
};

const ImageCollection = (props) => {
  // Adjust: if "item" exists, use it as config; otherwise, use props directly.
  const config = props.item ? props.item : props;
  const {
    images = [],
    width,
    height,
    max_width_pct_component, // number (e.g. 80 for 80%)
    auto_change = false,
    fit_adjustment = "cover", // 'cover', 'contain', or 'stretch'
    transition_style = "fade", // default value now among the choices
  } = config;

  // Get preview context for proper width resolution
  const preview = useContext(PreviewContext);

  // State for carousel
  const [currentIndex, setCurrentIndex] = useState(0);
  // State for fade transition
  const [fadeClass, setFadeClass] = useState("opacity-100");
  const [nextImageLoaded, setNextImageLoaded] = useState(false);

  // Auto-change logic
  useEffect(() => {
    if (!auto_change || images.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 3000); // Change every 3 seconds

    return () => clearInterval(interval);
  }, [auto_change, images.length]);

  // Additional auto-change for advanced transitions
  useEffect(() => {
    if (!auto_change || images.length <= 1 || transition_style === "fade") return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 4000); // Change every 4 seconds for slide transitions
    
    return () => clearInterval(interval);
  }, [auto_change, images.length, transition_style]);

  // New states: transitioning flag and slideTransition object for slide effect
  const [transitioning, setTransitioning] = useState(false);
  const [slideTransition, setSlideTransition] = useState({
    prevIndex: null,
    direction: null,
  });
  const duration = 400; // transition duration in ms

  const [imageLoaded, setImageLoaded] = useState(false);
  const currentSrc = images.length > 0 ? images[currentIndex]?.image : null;

  useEffect(() => {
    if (!currentSrc) return;
    setImageLoaded(false);
    const img = new window.Image();
    img.src = currentSrc;
    if (img.complete && img.naturalWidth > 0) {
      setImageLoaded(true);
    } else {
      img.onload = () => setImageLoaded(true);
      img.onerror = () => setImageLoaded(true);
    }
    return () => { img.onload = null; img.onerror = null; };
  }, [currentSrc]);

  // Compute average aspect ratio using provided dimensions or the image's natural dimensions.
  console.log(images);
  const validImages = images.filter((img) => {
    const { width, height } = getDimensions(img);
    return width && height;
  });
  console.log("validImages");
  console.log(validImages);
  const avgRatio = validImages.length
    ? validImages.reduce((sum, img) => {
        const { width: imgWidth, height: imgHeight } = getDimensions(img);
        return sum + imgWidth / imgHeight;
      }, 0) / validImages.length
    : 1;
  // Compute default width/height if not provided.
  let computedWidth = width;
  let computedHeight = height;
  if (!computedWidth && validImages.length) {
    const avgWidth =
      validImages.reduce((sum, img) => {
        const { width: imgWidth } = getDimensions(img);
        return sum + imgWidth;
      }, 0) / validImages.length;
    computedWidth = avgWidth;
  }
  // If height is missing, and max_width_pct_component is provided, use preview width
  if (!computedHeight && max_width_pct_component) {
    // Prefer preview contentWidth; fallback to viewport width, then window width
    let effectiveWidth = typeof window !== "undefined" ? window.innerWidth : 1920;
    if (preview && typeof preview.contentWidth === "number") {
      effectiveWidth = preview.contentWidth;
    } else if (preview && preview.mode !== "responsive" && preview.viewport?.width) {
      effectiveWidth = preview.viewport.width;
    }
    const containerWidth = effectiveWidth * (max_width_pct_component / 100);
    computedWidth = containerWidth;
    computedHeight = containerWidth / avgRatio;
  } else if (!computedHeight) {
    // fallback: use computedWidth / average ratio
    computedHeight = computedWidth / avgRatio;
  }

  // Handle null/undefined config gracefully
  if (!config) {
    return null;
  }

  // Updated transitionTo helper to support fade and slide transitions separately.
  const transitionTo = (updater) => {
    // Simple transition - just update the index
    setCurrentIndex((prev) =>
      typeof updater === "function" ? updater(prev) : updater,
    );
  };

  // Handlers for arrows using transitionTo
  const showPrev = () => {
    transitionTo((prev) => (prev - 1 + images.length) % images.length);
  };
  const showNext = () => {
    transitionTo((prev) => (prev + 1) % images.length);
  };

  // In render, adjust image rendering for slide transition.
  const commonImageStyle = {
    position: transition_style === "slide" ? "absolute" : "static",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: fit_adjustment,
    ...getTransitionStyles(transition_style),
  };

  // Define container inline style
  const containerInlineStyle = {
    position: "relative",
    width: computedWidth,
    height: computedHeight,
    overflow: "hidden",
    ...(transition_style === "fade" ? { backgroundColor: "black" } : {}),
  };
  // New: Apply baseStyle to container instead of each image
  const { className: containerBaseClass, style: containerBaseStyle } =
    getBaseStyleClasses(config.baseStyle || {});
  const containerStyle = { ...containerInlineStyle, ...containerBaseStyle };

  return (
    <div className={containerBaseClass} style={containerStyle}>
      {images.length > 0 &&
      transition_style === "slide" &&
      slideTransition.prevIndex !== null ? (
        <>
          <img
            src={images[slideTransition.prevIndex].image}
            alt={images[slideTransition.prevIndex].alt_text || ""}
            style={{
              ...commonImageStyle,
              transition: "transform 0.5s ease",
              transform: transitioning
                ? slideTransition.direction === "next"
                  ? "translateX(-100%)"
                  : "translateX(100%)"
                : "translateX(0)",
            }}
          />
          <img
            src={images[currentIndex].image}
            alt={images[currentIndex].alt_text || ""}
            style={{
              ...commonImageStyle,
              transition: "transform 0.5s ease",
              transform: transitioning
                ? "translateX(0)"
                : slideTransition.direction === "next"
                  ? "translateX(100%)"
                  : "translateX(-100%)",
            }}
          />
        </>
      ) : (
        images.length > 0 && (
          <img
            src={images[currentIndex].image}
            alt={images[currentIndex].alt_text || ""}
            style={
              transition_style === "fade"
                ? { ...commonImageStyle, opacity: transitioning ? 0 : 1 }
                : commonImageStyle
            }
          />
        )
      )}
      {/* Shimmer placeholder while image loads */}
      {currentSrc && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(200, 200, 200, 0.15)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            borderRadius: "inherit",
            overflow: "hidden",
            zIndex: 2,
            pointerEvents: "none",
            opacity: imageLoaded ? 0 : 1,
            transition: "opacity 0.3s ease-out",
          }}
          aria-hidden="true"
        >
          <span
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%)",
              animation: "progressive-img-shimmer 1.5s ease-in-out infinite",
            }}
          />
        </div>
      )}
      {/* Left Arrow */}
      <div className="arrow left" onClick={showPrev}>
        <div className="arrow-top"></div>
        <div className="arrow-bottom"></div>
      </div>
      {/* Right Arrow */}
      <div className="arrow right" onClick={showNext}>
        <div className="arrow-top"></div>
        <div className="arrow-bottom"></div>
      </div>
    </div>
  );
};

export default ImageCollection;
